import { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import {
  MsalProvider,
  useMsal,
  useAccount,
  MsalAuthenticationTemplate
} from '@azure/msal-react';
import { StyledEngineProvider } from '@mui/material/styles';
import { InteractionType } from '@azure/msal-browser';
import type { PublicClientApplication } from '@azure/msal-browser';
import { SnackbarProvider } from 'notistack';
import { useLocation } from 'react-router';

import FullScreenLoader from 'components/shared/FullScreenLoader';
import { FilterProvider } from 'context/FilterContext';
import { isPublic, publicRoutes } from 'helpers/routes-public';
import BreadcrumbCookies from 'components/shared/BreadCrumbCookies';
import ErrorComponent from 'components/shared/ErrorComponent';

import AppRouter from './routes/AppRouter';
import { GlobalProvider } from './context/GlobalContext';
import { ApiRequestProvider } from './context/ApiContext';
import { loginRequest } from './authConfig';

import './app.css';

const queryClient = new QueryClient({
  defaultOptions: { queries: { cacheTime: 10000, retry: 1 } }
});

type AppProps = {
  msalInstance: PublicClientApplication;
};

/**
 * App Component
 *
 * Main component for the application.
 *
 * @component
 * @param {Object} AppProps - The properties for the App component.
 * @param {Object} AppProps.msalInstance - MSAL instance for authentication
 * @returns {JSX.Element} The rendered App component.
 *
 * @example
 * <App msalInstance={msalInstance} />
 */
export default function App({ msalInstance }: AppProps): JSX.Element {
  const { pathname } = useLocation();
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0]);

  const breadcrumbLinks = [{ label: '', url: '*' }];

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME;
    localStorage.removeItem('onPage');
  }, []);

  useEffect(() => {
    const tokenRequest = {
      ...loginRequest,
      account
    };

    instance.acquireTokenSilent(tokenRequest).catch(() => {});
  }, [instance, account]);

  return (
    <MsalProvider instance={msalInstance}>
      {isPublic(pathname) ? (
        <SnackbarProvider autoHideDuration={1500} maxSnack={3} hideIconVariant>
          <QueryClientProvider client={queryClient}>
            <GlobalProvider>{publicRoutes[pathname]}</GlobalProvider>
          </QueryClientProvider>
          <BreadcrumbCookies links={breadcrumbLinks} showCookieMessage />
        </SnackbarProvider>
      ) : (
        <MsalAuthenticationTemplate
          authenticationRequest={{
            ...loginRequest,
            extraQueryParameters: {
              ui_locales: localStorage.getItem('lang') || 'es'
            }
          }}
          errorComponent={ErrorComponent}
          interactionType={InteractionType.Redirect}
          loadingComponent={FullScreenLoader}
        >
          <SnackbarProvider
            autoHideDuration={1500}
            maxSnack={3}
            hideIconVariant
          >
            <QueryClientProvider client={queryClient}>
              <GlobalProvider>
                <StyledEngineProvider injectFirst>
                  <ApiRequestProvider>
                    <FilterProvider>
                      <AppRouter />
                    </FilterProvider>
                  </ApiRequestProvider>
                </StyledEngineProvider>
              </GlobalProvider>
            </QueryClientProvider>
          </SnackbarProvider>
          <BreadcrumbCookies links={breadcrumbLinks} showCookieMessage />
        </MsalAuthenticationTemplate>
      )}
    </MsalProvider>
  );
}
